import {
  DragDropContext,
  DragDropState,
  DragSource,
  DropTarget,
} from '@wandb/weave/common/containers/DragDropContainer';
import {isFirefox} from '@wandb/weave/common/util/browser';
import classNames from 'classnames';
import _ from 'lodash';
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Resizable, ResizeCallbackData} from 'react-resizable';

import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import * as PanelTypes from '../../state/views/panel/types';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {skipTransition} from '../../util/animations';
import {isDraggingWithinSection, isPanel} from '../../util/panelbank';
import {
  getBoxDimensions,
  isMobile,
  panelOnActivePage,
} from '../../util/panelbankFlow';
import {DEFAULT_PANEL_SIZE} from '../../util/panelbankTypes';
import {PanelContainer} from '../Panel/PanelContainer';
import {EmptySectionMessageNoPanels} from './EmptyMessages';
import {
  getBoxPositionFromIndex,
  getClosestColumnCount,
  getDropIndex,
  getPanelBoxPosition,
  getSnappedWidth,
} from './panelBankFlowSectionUtils';
import {usePanelBankSectionPagination} from './PanelBankSectionPagination';
import {OrganizedSettings, PanelBankSectionComponentSharedProps} from './types';

/** These are the props that need to pass into the section component so we can use in the panel component */
type PanelPropsForFlowSection = {
  defaultMoveToSectionRef?: PanelBankSectionConfigTypes.Ref;
  inheritedSettings?: OrganizedSettings;
  shouldRenderContent?: boolean;
};

type AllPanelBankFlowSectionProps = PanelBankSectionComponentSharedProps &
  PanelPropsForFlowSection;

const PanelBankFlowSectionComp: React.FC<AllPanelBankFlowSectionProps> = ({
  panelBankSectionConfigRef,
  panelBankWidth,
  activePanelRefs,
  inactivePanelRefs,
  panelSearchResults = new Map<PanelTypes.Ref, boolean>(),
  panelSearchMatchCount = 0,
  movePanelBetweenSections: movePanel,
  isSectionNameSearchMatch,
  inheritedSettings,
  defaultMoveToSectionRef,
  readOnly,
  isProjectReadOnly,
  shouldRenderContent,
}: AllPanelBankFlowSectionProps) => {
  const {dragRef, dragging, dropRef, dragData, setDragData} =
    useContext(DragDropContext);
  const [resizingRefId, setResizingRefId] = useState<string | null>(null);

  const [resizingSectionHeight, setResizingSectionHeight] = useState<
    number | null
  >(null);

  const {
    currentPage,
    flowConfig,
    maxPage,
    panelsPerPage,
    panelsPerRow,
    setCurrentPage,
    updateFlowConfig,
    numTotalPanelsInSection,
  } = usePanelBankSectionPagination({
    activePanelCount: activePanelRefs.length,
    isSectionNameSearchMatch: !!isSectionNameSearchMatch,
    panelBankSectionConfigRef,
    panelSearchMatchCount,
  });

  const {rowsPerPage, gutterWidth} = flowConfig;
  const dragIndex = _.isEqual(
    dragData?.fromSectionRef,
    panelBankSectionConfigRef
  )
    ? dragData?.dragIndex
    : undefined;
  const dropIndex = _.isEqual(dropRef, panelBankSectionConfigRef)
    ? dragData?.dropIndex
    : undefined;
  const mobile = isMobile();
  const {boxWidth, boxHeight} = getBoxDimensions(panelBankWidth, flowConfig);

  const [resizingSize, setResizingSize] = useState<{
    width: number;
    height: number;
  }>({width: boxWidth, height: boxHeight});

  const {isSearching} = usePanelSearchDebouncedContext();

  const pageHeight = resizingSectionHeight
    ? resizingSectionHeight
    : numTotalPanelsInSection === 0 && dragData?.dropIndex == null
    ? 0
    : (numTotalPanelsInSection <= panelsPerRow ? 1 : rowsPerPage) *
      (gutterWidth + boxHeight);

  // renderPanels is the same length as activePanels, but non-visible panels are set to false
  // This is so panels maintain the same index -- otherwise, panels get unnecessarily re-rerendered
  // TODO: ^ is this still true? the 'false' placeholder thing is pretty weird
  // TODO(perf): this is a good candidate for perf improvement
  // TODO: consolidate this with usePanelBankSectionList()
  const panelRefsToRender = useMemo(() => {
    const allPanelRefs = isSearching
      ? isSectionNameSearchMatch
        ? activePanelRefs
        : Array.from(panelSearchResults).reduce(
            (acc: PanelTypes.Ref[], [pr, isSearchMatch]) => {
              if (isSearchMatch) {
                acc.push(pr);
              }
              return acc;
            },
            []
          )
      : activePanelRefs;

    return allPanelRefs.map((pr, i) =>
      panelOnActivePage(i, currentPage, panelsPerPage) ? pr : false
    );
  }, [
    activePanelRefs,
    currentPage,
    isSearching,
    isSectionNameSearchMatch,
    panelSearchResults,
    panelsPerPage,
  ]);

  const dropPreviewPosition =
    dropIndex != null &&
    getBoxPositionFromIndex({
      boxPositionIndex: dropIndex,
      boxWidth,
      boxHeight,
      gutterWidth,
      panelsPerRow,
    });
  const draggingWithinSection = isDraggingWithinSection(
    panelBankSectionConfigRef,
    dragData
  );

  const noPanels = numTotalPanelsInSection + inactivePanelRefs.length === 0;

  const onDragOver = useCallback(
    (ctx: DragDropState, e: React.DragEvent) => {
      const sectionBounds = e.currentTarget.getBoundingClientRect();
      const mousePosition = {
        xPx: e.clientX - sectionBounds.left,
        yPx: currentPage * pageHeight + e.clientY - sectionBounds.top,
      };
      const newDropBoxIndex = Math.min(
        getDropIndex({
          boxHeight,
          boxWidth,
          gutterWidth,
          panelsPerRow,
          xPx: mousePosition.xPx,
          yPx: mousePosition.yPx,
        }),
        isDraggingWithinSection(panelBankSectionConfigRef, dragData) &&
          numTotalPanelsInSection > 0
          ? numTotalPanelsInSection - 1
          : numTotalPanelsInSection
      );
      if (newDropBoxIndex !== dragData?.dropIndex) {
        setDragData({
          ...(dragData ?? {}),
          dropIndex: newDropBoxIndex,
        });
      }
    },
    [
      currentPage,
      pageHeight,
      boxHeight,
      boxWidth,
      gutterWidth,
      panelsPerRow,
      panelBankSectionConfigRef,
      dragData,
      numTotalPanelsInSection,
      setDragData,
    ]
  );

  // If panelbank config changes, dispatch Event('resize') to trigger panel width recalculation
  const prevFlowConfigRef = useRef(flowConfig);
  useEffect(() => {
    const prevFlowConfig = prevFlowConfigRef.current;
    prevFlowConfigRef.current = flowConfig;
    if (_.isEqual(prevFlowConfig, flowConfig)) {
      return;
    }
    setTimeout(() => window.dispatchEvent(new Event('resize')), 1);
  }, [flowConfig]);

  // If you're on, say, page 3, and then you filter panels but there's only 1 page of results,
  // currentPage will be greater than maxPage, so we clamp it here
  useEffect(() => {
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [currentPage, maxPage, setCurrentPage]);

  const numVisiblePanels = useMemo(() => {
    return panelRefsToRender.filter(p => p !== false).length;
  }, [panelRefsToRender]);

  const onPanelResizeStop = useCallback(
    (e, data) => {
      const newColumnCount = getClosestColumnCount({
        flowConfig,
        panelBankWidth,
        pxWidth: data.size.width,
      });
      const newWidth = getSnappedWidth({
        panelBankWidth,
        flowConfig,
        snapToColumns: flowConfig.snapToColumns,
        w: data.size.width,
      });
      const newHeight = data.size.height;

      // Make sure to maintain the currently visible panels when resizing panel
      const newRowsPerPage = Math.ceil(numVisiblePanels / newColumnCount);

      updateFlowConfig({
        columnsPerPage: newColumnCount,
        boxWidth: newWidth,
        boxHeight: newHeight,
        rowsPerPage: newRowsPerPage,
      });
      setResizingSize({width: newWidth, height: newHeight});
      setResizingRefId(null);
    },
    [flowConfig, panelBankWidth, numVisiblePanels, updateFlowConfig]
  );

  const onPanelResize = useCallback(
    (e: React.SyntheticEvent<Element, Event>, data: ResizeCallbackData) => {
      setResizingSize({
        width: data.size.width,
        height: data.size.height,
      });
    },
    []
  );

  const panelResizePreviewStyle = useMemo(() => {
    if (mobile || resizingRefId == null) {
      return {display: 'none'};
    }
    const panelResizePreviewPosition = getBoxPositionFromIndex({
      boxPositionIndex:
        _.findIndex(panelRefsToRender, {id: resizingRefId}) % panelsPerPage,
      boxHeight,
      boxWidth,
      gutterWidth,
      panelsPerRow,
    });
    return {
      height: resizingSize.height,
      width: getSnappedWidth({
        flowConfig,
        panelBankWidth,
        snapToColumns: flowConfig.snapToColumns,
        w: resizingSize.width,
      }),
      transform:
        panelResizePreviewPosition == null
          ? undefined
          : `translate(${panelResizePreviewPosition[0] + gutterWidth}px, ${
              panelResizePreviewPosition[1]
            }px) `,
    };
  }, [
    boxHeight,
    boxWidth,
    flowConfig,
    gutterWidth,
    mobile,
    panelBankWidth,
    panelRefsToRender,
    panelsPerPage,
    panelsPerRow,
    resizingRefId,
    resizingSize.height,
    resizingSize.width,
  ]);

  return (
    <>
      <div className="resize-preview" style={panelResizePreviewStyle} />
      <Resizable
        width={NaN}
        height={noPanels ? DEFAULT_PANEL_SIZE : pageHeight}
        axis="y"
        onResize={(e, data) => {
          const maxRows = Math.ceil(numTotalPanelsInSection / panelsPerRow);
          updateFlowConfig({
            rowsPerPage: Math.max(
              1,
              Math.min(
                maxRows,
                Math.round(pageHeight / (boxHeight + gutterWidth))
              )
            ),
          });
          setResizingSectionHeight(data.size.height);
        }}
        onResizeStop={() => {
          const maxRows = Math.ceil(numTotalPanelsInSection / panelsPerRow);
          const firstVisiblePanelIndex = panelsPerPage * currentPage;
          const newRowsPerPage = Math.max(
            1,
            Math.min(
              maxRows,
              Math.round(pageHeight / (boxHeight + gutterWidth))
            )
          );
          const newCurrentPage = Math.floor(
            firstVisiblePanelIndex / (newRowsPerPage * panelsPerRow)
          );
          updateFlowConfig({rowsPerPage: newRowsPerPage});
          setCurrentPage(newCurrentPage);
          setResizingSectionHeight(null);
        }}>
        <div
          className={`flow-section ${
            resizingSectionHeight != null ? 'resizing-section' : ''
          } ${resizingRefId != null ? 'resizing-panel' : ''}
          ${mobile ? 'mobile' : ''}`}
          style={{
            height: noPanels ? DEFAULT_PANEL_SIZE : pageHeight,
          }}>
          <DropTarget
            className="flow-section__page"
            style={{
              // HAX: While dragging, we extend the drop target to overlap the section title so that
              // rearranging panels still works when the panel is dropped in the title area.
              // This assumes that the height of the section title area is 48px.
              height: noPanels
                ? DEFAULT_PANEL_SIZE
                : pageHeight + (dragging ? 48 : 0),
              paddingTop: dragging ? 48 : 0,
              top: -(dragging ? 48 : 0),
              overflow: 'initial',
              zIndex: 1,
            }}
            partRef={panelBankSectionConfigRef}
            isValidDropTarget={ctx => isPanel(ctx.dragRef)}
            onDragOver={onDragOver}
            onDrop={() => {
              if (
                movePanel != null &&
                dragRef &&
                dragData &&
                dropIndex != null
              ) {
                // Unfortunately movePanel is optional because of the way panelbank is reused
                // in reports. But it's always passed for PanelBankFlowSection, which is
                // only used in workspaces.
                movePanel(
                  dragRef as PanelTypes.Ref,
                  dragData.fromSectionRef as PanelBankSectionConfigTypes.Ref,
                  panelBankSectionConfigRef,
                  dropIndex,
                  new Set(inactivePanelRefs.map(r => r.id))
                );
              }
            }}>
            {noPanels ? (
              <EmptySectionMessageNoPanels />
            ) : (
              <div
                style={{
                  position: 'relative',
                  transform: `translateY(-${currentPage * pageHeight}px)`,
                }}>
                {dropPreviewPosition && (
                  <div
                    key="drop-preview"
                    className="drop-preview"
                    style={{
                      width: boxWidth,
                      height: boxHeight,
                      transform: `translate(${dropPreviewPosition[0]}px, ${dropPreviewPosition[1]}px) `,
                    }}
                  />
                )}
                {panelRefsToRender.map(panelRef => {
                  if (!panelRef) {
                    return null;
                  }
                  const panelRefId = panelRef.id;
                  const isResizing = resizingRefId === panelRefId;
                  const selectedForDrag =
                    dragRef && _.isEqual(dragRef, panelRef);

                  const {
                    newBoxPosition: boxPosition,
                    boxPositionIdxWhileDragging: boxPositionIndex,
                  } = getPanelBoxPosition({
                    boxHeight,
                    boxWidth,
                    draggingWithinSection,
                    dragIndex,
                    dropIndex,
                    gutterWidth,
                    panelRefId,
                    panelRefsToRender,
                    panelsPerRow,
                    selectedForDrag: !!selectedForDrag,
                  });

                  return (
                    <React.Fragment key={panelRefId}>
                      <Resizable
                        key={panelRefId}
                        className={classNames('panel-bank__panel', {
                          // 300 so the draggable handle does not overlap with action icons
                          resizing: isResizing,
                          dragging: selectedForDrag && dragging,
                        })}
                        axis={mobile ? undefined : 'both'}
                        width={resizingSize.width}
                        height={resizingSize.height}
                        minConstraints={[
                          DEFAULT_PANEL_SIZE,
                          DEFAULT_PANEL_SIZE,
                        ]}
                        maxConstraints={[
                          panelBankWidth - 2 * gutterWidth,
                          panelBankWidth,
                        ]}
                        onResize={onPanelResize}
                        onResizeStop={onPanelResizeStop}
                        onResizeStart={() => setResizingRefId(panelRefId)}>
                        <DragSource
                          key={panelRefId}
                          partRef={panelRef}
                          data={{
                            fromSectionRef: panelBankSectionConfigRef,
                            dragIndex: boxPositionIndex,
                          }}
                          onMouseUp={e => {
                            skipTransition(e.currentTarget, 50);
                          }}
                          style={{
                            width: boxWidth,
                            height: boxHeight,
                            ...((selectedForDrag && !dragging) || isFirefox
                              ? {
                                  left: boxPosition[0],
                                  top: boxPosition[1],
                                }
                              : {
                                  transform: `translate(${boxPosition[0]}px, ${boxPosition[1]}px)`,
                                }),
                          }}>
                          <PanelContainer
                            panelRef={panelRef}
                            panelBankSectionConfigRef={
                              panelBankSectionConfigRef
                            }
                            inheritedSettings={inheritedSettings}
                            shouldRenderContent={shouldRenderContent}
                            defaultMoveToSectionRef={defaultMoveToSectionRef}
                            isReadOnly={readOnly}
                            isProjectReadOnly={isProjectReadOnly}
                          />
                        </DragSource>
                      </Resizable>
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </DropTarget>
        </div>
      </Resizable>
    </>
  );
};

export const PanelBankFlowSection = memo(PanelBankFlowSectionComp);
